import React, { useState, useEffect, useCallback } from "react";
import { Eye, Mail, TrendingUp, Calendar, Lock, MessageSquare, AlertCircle, Download } from "lucide-react";
import { API_BASE_URL } from "../config/api";

const AnalyticsStats = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  const [stats, setStats] = useState({
    welcomeGate: { total_views: 0, last_viewed: null },
    subscribers: { count: 0 },
    subscriberList: [],
    messages: [],
    unreadMessages: 0,
    epiicHero: { total_downloads: 0, today_downloads: 0, week_downloads: 0, recent_downloads: [] },
    vowWitnesses: { total: 0, by_pillar: { YAH: 0, HEALTH: 0, WEALTH: 0, HAPPINESS: 0 }, recent: [] },
    loading: true,
    error: null
  });

  const fetchStats = useCallback(async () => {
    const token = localStorage.getItem('analytics_token');
    if (!token) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [welcomeGateRes, subscribersRes, subscriberListRes, messagesRes, epiicHeroRes, vowsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/analytics/welcome-gate/stats`, { headers }),
        fetch(`${API_BASE_URL}/api/subscribers/count/protected`, { headers }),
        fetch(`${API_BASE_URL}/api/subscribers/list`, { headers }),
        fetch(`${API_BASE_URL}/api/contact/messages`, { headers }),
        fetch(`${API_BASE_URL}/api/analytics/epiic-hero-stats`, { headers }),
        fetch(`${API_BASE_URL}/api/analytics/vow-witnesses/stats`)
      ]);

      if (welcomeGateRes.status === 401 || subscribersRes.status === 401) {
        localStorage.removeItem('analytics_token');
        setIsAuthenticated(false);
        setStats(prev => ({ ...prev, loading: false, error: "Session expired. Please login again." }));
        return;
      }

      const welcomeGateData = await welcomeGateRes.json();
      const subscribersData = await subscribersRes.json();
      const subscriberListData = await subscriberListRes.json();
      const messagesData = await messagesRes.json();
      const epiicHeroData = epiicHeroRes.ok ? await epiicHeroRes.json() : { total_downloads: 0, today_downloads: 0, week_downloads: 0, recent_downloads: [] };
      const vowsData = vowsRes.ok ? await vowsRes.json() : { total: 0, by_pillar: { YAH: 0, HEALTH: 0, WEALTH: 0, HAPPINESS: 0 }, recent: [] };

      setStats({
        welcomeGate: welcomeGateData,
        subscribers: subscribersData,
        subscriberList: subscriberListData.subscribers || [],
        messages: messagesData.messages || [],
        unreadMessages: messagesData.unread || 0,
        epiicHero: epiicHeroData,
        vowWitnesses: vowsData,
        loading: false,
        error: null
      });
    } catch (error) {
      setStats(prev => ({ ...prev, loading: false, error: error.message }));
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('analytics_token');
    if (token) {
      setIsAuthenticated(true);
      fetchStats();
      const interval = setInterval(fetchStats, 30000);
      return () => clearInterval(interval);
    } else {
      setStats(prev => ({ ...prev, loading: false }));
    }
  }, [isAuthenticated, fetchStats]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem('analytics_token', data.token);
        setIsAuthenticated(true);
        setPassword("");
      } else {
        setLoginError(data.message || "Invalid password");
      }
    } catch (error) {
      setLoginError("Connection error. Please try again.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('analytics_token');
    setIsAuthenticated(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-orange-900 to-black flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-md w-full">
          {/* Lock Icon */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-600 to-orange-500 rounded-full mb-4">
              <Lock size={40} className="text-white" />
            </div>
            <h1 className="text-3xl font-light text-gray-800 mb-2">
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 text-sm font-light">
              Enter password to access analytics
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter analytics password"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
                autoFocus
                data-testid="analytics-password-input"
              />
            </div>

            {/* Error Message */}
            {loginError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                {loginError}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoggingIn}
              data-testid="analytics-login-submit-btn"
              className={`w-full py-3 rounded-lg font-medium text-white transition-all ${
                isLoggingIn
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-600 to-orange-500 hover:shadow-lg hover:scale-105'
              }`}
            >
              {isLoggingIn ? (
                <span className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Logging in...</span>
                </span>
              ) : (
                'Access Dashboard'
              )}
            </button>
          </form>

          {/* Back to Portfolio */}
          <div className="mt-8 text-center">
            <a
              href="/"
              className="text-sm text-gray-600 hover:text-purple-600 transition-colors"
            >
              ← Back to Portfolio
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (stats.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-light">Loading Analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <section className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 py-24">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-start mb-6">
            <div className="flex-1"></div>
            <div className="inline-flex items-center space-x-3">
              <TrendingUp size={32} className="text-purple-600" />
            </div>
            <div className="flex-1 flex justify-end">
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-light hover:bg-gray-300 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
          <h1 className="text-5xl lg:text-6xl font-light tracking-tight text-black mb-4">
            <span className="bg-gradient-to-r from-purple-600 via-orange-500 to-yellow-500 bg-clip-text text-transparent">
              Analytics Dashboard
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-light">
            Real-time insights into portfolio engagement and subscriber growth
          </p>
        </div>

        {/* Error Message */}
        {stats.error && (
          <div className="mb-8 bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg">
            <p className="font-medium">Error loading analytics:</p>
            <p className="text-sm">{stats.error}</p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* EPIIC Hero Kit Downloads - PRIMARY */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-yellow-500">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <Download size={28} className="text-yellow-600" />
                </div>
                <h2 className="text-2xl font-light text-gray-800">EPIIC Hero Kit</h2>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Total Downloads</p>
                <p className="text-4xl font-light text-yellow-600" data-testid="epiic-hero-total-downloads">
                  {stats.epiicHero.total_downloads.toLocaleString()}
                </p>
              </div>

              <div className="border-t pt-4 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Today</p>
                  <p className="text-2xl font-light text-green-600" data-testid="epiic-hero-today-downloads">
                    {stats.epiicHero.today_downloads}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">This Week</p>
                  <p className="text-2xl font-light text-blue-600" data-testid="epiic-hero-week-downloads">
                    {stats.epiicHero.week_downloads}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Welcome Gate Stats */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-purple-500">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Eye size={28} className="text-purple-600" />
                </div>
                <h2 className="text-2xl font-light text-gray-800">Welcome Gate</h2>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Total Views</p>
                <p className="text-4xl font-light text-purple-600">
                  {stats.welcomeGate.total_views.toLocaleString()}
                </p>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Calendar size={16} />
                  <p>Last Viewed:</p>
                </div>
                <p className="text-sm text-gray-700 font-light mt-1">
                  {formatDate(stats.welcomeGate.last_viewed)}
                </p>
              </div>
            </div>
          </div>

          {/* Subscribers Stats */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border-t-4 border-orange-500">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <Mail size={28} className="text-orange-600" />
                </div>
                <h2 className="text-2xl font-light text-gray-800">INSPIYAH MAIL</h2>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Total Subscribers</p>
                <p className="text-4xl font-light text-orange-600">
                  {stats.subscribers.count.toLocaleString()}
                </p>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-600">
                  Active email subscribers connected to the Fiyah
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Engagement Overview */}
        <div className="bg-gradient-to-r from-purple-600 via-orange-500 to-yellow-500 rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-light mb-4">Engagement Summary</h3>
          <div className="grid md:grid-cols-4 gap-6">
            <div>
              <p className="text-white/80 text-sm uppercase tracking-wide mb-1">EPIIC Downloads</p>
              <p className="text-3xl font-light">{stats.epiicHero.total_downloads}</p>
              <p className="text-white/70 text-xs mt-1">Hero Kit Captures</p>
            </div>
            
            <div>
              <p className="text-white/80 text-sm uppercase tracking-wide mb-1">Conversion Rate</p>
              <p className="text-3xl font-light">
                {stats.welcomeGate.total_views > 0
                  ? ((stats.subscribers.count / stats.welcomeGate.total_views) * 100).toFixed(1)
                  : 0}%
              </p>
              <p className="text-white/70 text-xs mt-1">Visitors to Subscribers</p>
            </div>
            
            <div>
              <p className="text-white/80 text-sm uppercase tracking-wide mb-1">Total Visitors</p>
              <p className="text-3xl font-light">{stats.welcomeGate.total_views.toLocaleString()}</p>
              <p className="text-white/70 text-xs mt-1">Welcome Gate Views</p>
            </div>
            
            <div>
              <p className="text-white/80 text-sm uppercase tracking-wide mb-1">Connected Souls</p>
              <p className="text-3xl font-light">{stats.subscribers.count.toLocaleString()}</p>
              <p className="text-white/70 text-xs mt-1">INSPIYAH MAIL Family</p>
            </div>
          </div>
        </div>

        {/* ─── ITERATION 6.1 — VOW WITNESSES (Pillar Pulse) ─── */}
        <div className="mt-8 bg-gradient-to-br from-[#050505] via-[#0a0a0a] to-[#1a1a1a] rounded-2xl p-8 text-white border border-[#B87333]/30 shadow-[0_0_40px_rgba(184,115,51,0.15)]" data-testid="vow-witnesses-widget">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#B87333] mb-1">Pillar Pulse</p>
              <h3 className="text-2xl font-light tracking-wider">Vow Witnesses</h3>
              <p className="text-xs text-gray-500 mt-1">Which pillar resonates most with seekers</p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-light text-[#D4AF37]" data-testid="vow-witnesses-total">
                {stats.vowWitnesses.total.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 uppercase tracking-wider mt-1">Total</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'YAH', color: '#D4AF37', label: 'Spiritual Source' },
              { name: 'HEALTH', color: '#4ADE80', label: 'The Sacred Vessel' },
              { name: 'WEALTH', color: '#B87333', label: 'Abundance' },
              { name: 'HAPPINESS', color: '#F472B6', label: 'Joy in Purpose' },
            ].map(p => {
              const count = stats.vowWitnesses.by_pillar[p.name] || 0;
              const total = stats.vowWitnesses.total || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div
                  key={p.name}
                  className="relative p-5 transition-all duration-300"
                  style={{ background: 'rgba(0,0,0,0.4)', border: `1px solid ${p.color}40` }}
                  data-testid={`vow-pillar-stat-${p.name.toLowerCase()}`}
                >
                  <p className="text-xs tracking-[0.3em] uppercase mb-2" style={{ color: p.color }}>{p.name}</p>
                  <p className="text-3xl font-light mb-1" style={{ color: p.color }}>{count}</p>
                  <p className="text-[10px] tracking-wider uppercase text-gray-500 mb-3">{p.label}</p>
                  <div className="h-1 w-full" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <div
                      className="h-full transition-all duration-700"
                      style={{ width: `${stats.vowWitnesses.total > 0 ? pct : 0}%`, background: p.color }}
                    />
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">{stats.vowWitnesses.total > 0 ? `${pct}%` : '—'}</p>
                </div>
              );
            })}
          </div>

          {stats.vowWitnesses.recent && stats.vowWitnesses.recent.length > 0 && (
            <div className="mt-6 pt-6 border-t border-[#B87333]/15">
              <p className="text-xs tracking-[0.3em] uppercase text-gray-500 mb-3">Recent Witnesses</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {stats.vowWitnesses.recent.map((w, i) => (
                  <div key={i} className="flex justify-between items-center text-sm py-1">
                    <span className="text-gray-300 font-mono text-xs truncate flex-1 mr-3">{w.email}</span>
                    <span className="text-xs px-2 py-1" style={{
                      color: { YAH: '#D4AF37', HEALTH: '#4ADE80', WEALTH: '#B87333', HAPPINESS: '#F472B6' }[w.pillar] || '#888',
                      border: `1px solid ${{ YAH: '#D4AF37', HEALTH: '#4ADE80', WEALTH: '#B87333', HAPPINESS: '#F472B6' }[w.pillar] || '#444'}40`,
                    }}>
                      {w.pillar}
                    </span>
                    <span className="text-gray-600 text-xs ml-3 hidden md:inline">{formatDate(w.witnessed_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Recent EPIIC Hero Downloads */}
        {stats.epiicHero.recent_downloads && stats.epiicHero.recent_downloads.length > 0 && (
          <div className="mt-8">
            <h2 className="text-2xl font-light text-gray-800 mb-4 flex items-center">
              <Download size={24} className="mr-3 text-yellow-600" />
              Recent EPIIC Hero Kit Downloads
            </h2>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4">
                <div className="grid grid-cols-4 gap-4 text-white font-medium text-sm">
                  <div>Name</div>
                  <div>Email</div>
                  <div>Phone</div>
                  <div>Date</div>
                </div>
              </div>
              <div className="divide-y divide-gray-200 max-h-64 overflow-y-auto">
                {stats.epiicHero.recent_downloads.map((dl, idx) => (
                  <div 
                    key={dl.email || idx} 
                    className="grid grid-cols-4 gap-4 px-6 py-3 hover:bg-yellow-50 transition-colors"
                  >
                    <div className="text-gray-800 font-light truncate">{dl.name}</div>
                    <div className="text-gray-600 text-sm font-light truncate">{dl.email}</div>
                    <div className="text-gray-600 text-sm font-light">{dl.phone}</div>
                    <div className="text-gray-600 text-sm font-light">{formatDate(dl.subscribed_at)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Contact Messages Section - NEW */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-light text-gray-800 flex items-center">
              <MessageSquare size={32} className="mr-3 text-orange-600" />
              Contact Messages
            </h2>
            {stats.unreadMessages > 0 && (
              <div className="flex items-center space-x-2 bg-red-100 text-red-700 px-4 py-2 rounded-full">
                <AlertCircle size={20} />
                <span className="font-medium">{stats.unreadMessages} Unread</span>
              </div>
            )}
          </div>
          
          {stats.messages && stats.messages.length > 0 ? (
            <div className="space-y-4">
              {stats.messages.map((message, index) => (
                <div 
                  key={message.email + '-' + (message.submitted_at || index)} 
                  className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${
                    message.status === 'unread' 
                      ? 'border-orange-500 bg-orange-50/30' 
                      : 'border-gray-300'
                  } hover:shadow-xl transition-shadow`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-medium text-gray-800">{message.name}</h3>
                        {message.status === 'unread' && (
                          <span className="px-3 py-1 bg-orange-500 text-white text-xs rounded-full font-medium">
                            NEW
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex items-center">
                          <Mail size={14} className="mr-1" />
                          {message.email}
                        </span>
                        <span className="flex items-center">
                          <Calendar size={14} className="mr-1" />
                          {formatDate(message.submitted_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Subject:</p>
                    <p className="text-lg font-medium text-gray-800">{message.subject}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">Message:</p>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{message.message}</p>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                    <span>Source: {message.source || 'contact_form'}</span>
                    {message.read_at && (
                      <span>Read: {formatDate(message.read_at)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 font-light text-lg">
                No messages yet
              </p>
              <p className="text-gray-500 font-light text-sm mt-2">
                Messages from the contact form will appear here
              </p>
            </div>
          )}
        </div>

        {/* Subscriber List Section */}
        <div className="mt-12">
          <h2 className="text-3xl font-light text-gray-800 mb-6 text-center">
            📧 Subscriber List
          </h2>
          
          {stats.subscriberList && stats.subscriberList.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Table Header */}
              <div className="bg-gradient-to-r from-purple-600 to-orange-500 px-6 py-4">
                <div className="grid grid-cols-3 gap-4 text-white font-medium text-sm">
                  <div>Email</div>
                  <div>Subscribed Date</div>
                  <div>Source</div>
                </div>
              </div>
              
              {/* Table Body */}
              <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                {stats.subscriberList.map((subscriber, index) => (
                  <div 
                    key={subscriber.email || index} 
                    className="grid grid-cols-3 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="text-gray-800 font-light truncate">
                      {subscriber.email}
                    </div>
                    <div className="text-gray-600 text-sm font-light">
                      {formatDate(subscriber.subscribed_at)}
                    </div>
                    <div className="text-gray-600 text-sm font-light">
                      <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                        {subscriber.source || 'website'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Footer */}
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-600 font-light">
                  Total subscribers: <strong>{stats.subscriberList.length}</strong>
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Mail size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 font-light text-lg">
                No subscribers yet
              </p>
              <p className="text-gray-500 font-light text-sm mt-2">
                Subscribers will appear here once they sign up via INSPIYAH MAIL
              </p>
            </div>
          )}
        </div>

        {/* Auto-refresh Notice */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 font-light">
            📊 Stats auto-refresh every 30 seconds • Last updated: {new Date().toLocaleTimeString()}
          </p>
          <button
            onClick={fetchStats}
            className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-full text-sm font-light hover:bg-purple-700 transition-colors"
          >
            Refresh Now
          </button>
        </div>

        {/* Back to Home */}
        <div className="mt-12 text-center">
          <a
            href="/"
            className="inline-block px-8 py-3 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-light tracking-wide hover:shadow-lg transition-all hover:scale-105"
          >
            Back to Portfolio
          </a>
        </div>
      </div>
    </section>
  );
};

export default AnalyticsStats;
